import moment from 'moment'
import cookie from '@/services/cookie'
import sib from '@/services/sib'
import zoho from '@/services/zoho'
import mixpanelUtils from '@/utils/mixpanel.utils'

import UserApi from '@/api/user'
import ABTestApi from '@/api/abTest'
import AccountApi from '@/api/account'
import utils from '@/util'

import constants from '@/constant'
import PlanAPI from '@/api/plan'
import sentry from '@/services/sentry'

const { eventCategories, AB_TEST } = constants

const AB_TEST_CODE = {
  FRENCH: 'LOCALIZATION_FRENCH',
}
const USER_COUNTRY = {
  FRENCH: 'France',
}
const FRENCH_AB_TEST_GROUP = {
  CONTROL: 'CONTROL',
  TREATMENT: 'TREATMENT',
}
const setCurrentUserAction = async ({ commit }, payload) => {
  commit('setCurrentUser', payload)
}

const setPlan = async ({ commit }, payload) => {
  commit('setPlan', payload)
}

const updateCurrentABTestAction = async ({ commit }, payload) => {
  commit('updateCurrentABTest', payload)
}

const callApiGetABTest = async (abTestCode) => {
  try {
    return ABTestApi.getTest(abTestCode)
  } catch (e) {
    sentry.captureException(e)
    return false
  }
}

const handleFrenchLanguageABtest = async ({ commit }, currentUser) => {
  if (currentUser && currentUser.personalInformation &&
    currentUser.personalInformation.presenterLanguage &&
    currentUser.personalInformation.presenterLanguage !== 'fr') return
  if (currentUser.country !== USER_COUNTRY.FRENCH) {
    return
  }
  const userAlreadyBelongToThisExperiment = currentUser.abTest && currentUser.abTest[AB_TEST_CODE.FRENCH]
  if (userAlreadyBelongToThisExperiment) {
    return
  }

  const resGetAbTest = await callApiGetABTest(AB_TEST_CODE.FRENCH)
  if (!resGetAbTest || !resGetAbTest.body) {
    return
  }

  const { testCode, variant } = resGetAbTest.body
  const hasABTestData = testCode && variant
  if (!hasABTestData) {
    return
  }

  const newExperiment = {
    [AB_TEST_CODE.FRENCH]: variant,
  }
  commit('updateCurrentUser', newExperiment)
}

const updateLocalStorageAndUserAndSettings = async (currentUser) => {
  if (!window.localStorage.getItem(`localizeI18n-${currentUser.id}`)) {
    window.localStorage.setItem(`localizeI18n-${currentUser.id}`, constants.presenterLanguage.fr)
  }
  const updatePresenterLanguageCondition = (
    currentUser &&
    (!currentUser.personalInformation ||
      (currentUser.personalInformation && !currentUser.personalInformation.presenterLanguage))
  )
  if (updatePresenterLanguageCondition) {
    await UserApi.patchUserById(currentUser.id, {
      personalInformation: {
        presenterLanguage: constants.presenterLanguage.fr,
      },
      rememberSettings: {
        language: constants.presenterLanguage.fr,
      },
    })
    currentUser.rememberSettings.language = constants.presenterLanguage.fr
    if (!currentUser.personalInformation) currentUser.personalInformation = {}
    currentUser.personalInformation.presenterLanguage = constants.presenterLanguage.fr
  }
  return currentUser
}
const getCurrentUserAction = async ({ commit, state, dispatch }) => {
  let { currentUser } = state

  try {
    // phải lấy profile 2 lần nếu k thì k thì có bug khi signup từ form ở trang chủ
    // http://localhost:8080/pages/token?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6MzU
    // sImlhdCI6MTYzMjIxODAyNiwiZXhwIjoxNjk1MjkwMDI2fQ.dOpl8ICE7i-2624J5pmVQJdO4mcBpUgJkE
    // 7UkvZAlOI&signup=1&name=loi&email=stove+102test@gmail.com
    if (!currentUser.id) {
      currentUser = await UserApi.getUser()
    }
  } catch (error) {
    commit('setIsServerConnectable', false)

    utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'User > Load > Failed')
    throw error
  }

  currentUser.rememberSettings = currentUser.rememberSettings || {}
  if (currentUser.accountId) {
    dispatch('getCurrentTeamAction', currentUser.accountId)
  }
  if (currentUser.id && currentUser.plan !== 'white-label') {
    currentUser.isPlayingDemoEnabled = false
    if (!currentUser.playedPresentationIds ||
      (currentUser.playedPresentationIds && !currentUser.playedPresentationIds.length)) {
      currentUser.isPlayingDemoEnabled = true
    }
  }

  const payload = await PlanAPI.getMyPlan().then((res) => res.body)
  if (payload && payload.subscription && payload.subscription.current_period_end) {
    currentUser.current_period_end = payload.subscription.current_period_end
  }
  const TIME_GO_LIVE = 'Mon Nov 13 2023 09:50:34 GMT+0700'
  if (currentUser.startDateFirstPlan) {
    currentUser.isBeforeCurrentPeriodStart = moment(TIME_GO_LIVE).isBefore(moment(currentUser.startDateFirstPlan * 1000))
  } else if (currentUser.plan === 'free') {
    currentUser.isBeforeCurrentPeriodStart = true
  }

  commit('setCurrentUser', currentUser)
  commit('setPlan', currentUser.plan)
  await handleFrenchLanguageABtest({ commit }, currentUser)

  const isPresenterLanguageWasFrench = (
    !currentUser.personalInformation ||
    (
      currentUser.personalInformation &&
        (
          !currentUser.personalInformation.presenterLanguage ||
          currentUser.personalInformation.presenterLanguage === constants.presenterLanguage.fr
        )
    )
  )
  const isTreatmentFrenchLocalizeState = (currentUser.abTest
    && currentUser.abTest[AB_TEST_CODE.FRENCH] === FRENCH_AB_TEST_GROUP.TREATMENT)
  const isTreatmentFrenchLocalizeLocalstorage = window.localStorage.getItem(`localizeI18n-${currentUser.id}`) === constants.presenterLanguage.fr
  if ((isTreatmentFrenchLocalizeState || isTreatmentFrenchLocalizeLocalstorage) && isPresenterLanguageWasFrench) {
    dispatch('setCurrentUserI18nLang', constants.presenterLanguage.fr)
    currentUser = await updateLocalStorageAndUserAndSettings(currentUser)
  }
  // store user id into for logging purpose
  cookie.setValue('aha-user-id', currentUser.id)
  cookie.setValueByDomain('aha-user-id', currentUser.id)

  sentry.identifyUser(currentUser)
  sib.identifyUser(currentUser)
  zoho.identifyUser(currentUser)

  // TODO: should simplify with one funcion call like above
  mixpanelUtils.identify(currentUser)

  // mixpanelUtils.track('Access presenter app')

  return currentUser
}

const getCurrentTeamAction = async ({ commit }, teamId) => {
  try {
    const currentAccount = await AccountApi.getAccountById(teamId)
    if (!currentAccount.id) currentAccount.id = 0
    commit('setCurrentAccount', currentAccount)
  } catch (error) {
    if (error.status === 403) {
      commit('setCurrentAccount', {})
    }
  }
}

const setCurrentUserI18nLang = async ({ commit }, country) => {
  commit('setPresenterLang', country)
}
const setCurrentUserHasCreatedPresentationAction = async ({ commit, state }) => {
  const userHasCreatedPresentation = state.currentUser.personalInformation &&
        state.currentUser.personalInformation.hasCreatedPresentation

  if (!userHasCreatedPresentation) {
    const res = await UserApi.patchUserById(state.currentUser.id, {
      personalInformation: {
        ...state.currentUser.personalInformation,
        hasCreatedPresentation: true,
      },
    })

    const patchedUser = {
      ...state.currentUser,
      ...res.body,
    }

    commit('setHasOnboardingV3', false)
    commit('setCurrentUser', patchedUser)
  }
}

const updateCurrentUserAction = async ({ commit }, payload) => {
  commit('updateCurrentUser', payload)
}

const setUpABTestPlanUpgradeEditorAction = async ({ commit }) => {
  const resGetABTest = await callApiGetABTest(AB_TEST.PLAN_UPGRADE_EDITOR)

  if (!resGetABTest || !resGetABTest.body) {
    return false
  }

  const { testCode, variant } = resGetABTest.body
  const hasABTestData = testCode && variant

  if (!hasABTestData) {
    return false
  }

  const payload = {}
  payload[AB_TEST.PLAN_UPGRADE_EDITOR] = variant
  commit('updateCurrentABTest', payload)

  return payload
}

const setupABTestNewSlideTypePreviewAction = async ({ commit }) => {
  const resGetABTest = await callApiGetABTest(AB_TEST.SLIDE_TYPE_PREVIEW)

  if (!resGetABTest || !resGetABTest.body) {
    return false
  }

  const { testCode, variant } = resGetABTest.body
  const hasABTestData = testCode && variant

  if (!hasABTestData) {
    return false
  }

  const payload = {}
  payload[AB_TEST.SLIDE_TYPE_PREVIEW] = variant
  commit('updateCurrentABTest', payload)

  return payload
}

export default {
  getCurrentUserAction,
  setCurrentUserAction,
  setPlan,
  setCurrentUserHasCreatedPresentationAction,
  updateCurrentUserAction,
  updateCurrentABTestAction,
  getCurrentTeamAction,
  setCurrentUserI18nLang,
  setupABTestNewSlideTypePreviewAction,
  setUpABTestPlanUpgradeEditorAction,
}
